/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import ReactDOM from 'react-dom';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Button } from '@carbon/react';

import { Link as Hyperlink } from 'primitives';
import { Dropdown, TargetSelector } from 'components';
import { commentsStore, currentDiagramStore, notificationStore } from 'stores';
import { selectElementAndFocusField } from 'App/Pages/Diagram/properties-panel-util';
import { folderService, trackingService } from 'services';
import buildSlug from 'utils/buildSlug';
import {
  createOrUpdateExtensionElement,
  removeExtensionElement
} from 'utils/web-modeler-diagram-parser/extension-elements-util';
import * as Styled from 'App/Pages/Diagram/LinkOverlay.styled';
import Tooltip from 'App/Pages/Diagram/Tooltip';

import callActivityLinkStore from './CallActivityLinkStore';

const Overlay = observer(({ element, readOnly }) => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [linkedDiagrams, setLinkedDiagrams] = useState({});
  const [contextDetails, setContextDetails] = useState(null);
  const { project, diagram: currentDiagram } = currentDiagramStore.state;
  const { isElementLinked } = callActivityLinkStore;

  const getLinkedDiagrams = async () => {
    const diagrams = await callActivityLinkStore.getExistingLinks(element);
    setLinkedDiagrams(diagrams);
  };

  const getProjectDetails = async () => {
    const updatedProject = await currentDiagramStore.updateProject(project.id);
    setContextDetails(updatedProject);
  };

  const getBrowsableContextDetails = async () => {
    const context = await folderService.fetchById(currentDiagramStore.state.diagram.folderId);
    setContextDetails(context);
  };

  const modeler = currentDiagramStore.modeler;

  const contextPad = modeler.get('contextPad'),
    eventBus = modeler.get('eventBus');

  eventBus.on('contextPad.linkResource', (context) => {
    const { element, originalEvent } = context;

    if (contextPad.isOpen(element)) {
      handleClick(originalEvent);
    }
  });

  useEffect(() => {
    if (anchorEl) {
      if (isElementLinked(element)) {
        getLinkedDiagrams();
      } else if (currentDiagramStore.ofProcessApplicationOrFolder) {
        getBrowsableContextDetails();
      } else {
        getProjectDetails();
      }
    }
  }, [anchorEl, element]);

  const trackResourceLinking = (action) => {
    trackingService.trackResourceLinking({
      action,
      resource: 'BPMN',
      from: 'BPMN'
    });
  };

  const linkProcess = (diagram) => {
    createOrUpdateExtensionElement(element, 'zeebe:CalledElement', { processId: diagram.processId }, modeler);

    focusTargetProcessIdField();

    notificationStore.showSuccess('Call activity has been successfully linked.');

    trackResourceLinking('link');

    setAnchorEl(null);
  };

  const unlinkProcess = () => {
    removeExtensionElement(element, 'zeebe:CalledElement', modeler);

    focusTargetProcessIdField();

    notificationStore.showSuccess('Call activity has been unlinked.');

    trackResourceLinking('unlink');

    setAnchorEl(null);
  };

  const focusTargetProcessIdField = async () => {
    // TODO(philippfromme): layout should be kept in a separate store
    commentsStore.makePropertiesVisible();
    await selectElementAndFocusField({
      modeler: currentDiagramStore.modeler,
      element: element,
      groupId: 'group-calledElement',
      fieldId: '#bio-properties-panel-targetProcessId'
    });
  };

  const deselect = () => {
    const modeler = currentDiagramStore.modeler,
      selection = modeler.get('selection');
    selection?.deselect(element);
  };

  const handleClick = (event) => {
    // directEditing and popupMenu are not available in navigated viewer (used in read-only mode)
    currentDiagramStore.modeler?.get('directEditing')?.complete();
    currentDiagramStore.modeler.get('popupMenu', false)?.close();

    if (!anchorEl) {
      trackResourceLinking('openMenu');
    }

    let target = event.delegateTarget || event.target;

    if (target.closest('.djs-context-pad')) {
      target = target.closest('.djs-context-pad');
    }

    setAnchorEl(anchorEl ? null : target);
  };

  // @ts-expect-error TS2339
  const { links, broken, expression } = linkedDiagrams;
  const isCalledProcessPresent = links?.length > 0 || broken || expression;

  return (
    <Styled.Container>
      <Tooltip label="Link process">
        <Styled.LinkButton onClick={handleClick} data-test="call-activity-link-button" />
      </Tooltip>
      {isElementLinked(element) ? (
        <Dropdown
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          width={360}
          align="left"
          noMargin
          noPadding
        >
          {/* @ts-expect-error TS2769 */}
          <Dropdown.Title noPadding>Calls process:</Dropdown.Title>
          {isCalledProcessPresent ? (
            <>
              {links?.length > 0 && (
                <Styled.LinkContainer>
                  <ul>
                    {links?.map((diagram) => (
                      <li key={diagram.id}>
                        <Styled.Link
                          data-test={`linked-diagram-${diagram.name}`}
                          onClick={() => {
                            if (currentDiagram?.id === diagram.id) {
                              deselect();
                              setAnchorEl(null);
                            }
                            history.push(`/diagrams/${buildSlug(diagram.id, diagram.name)}`);
                          }}
                        >
                          {diagram.name}
                        </Styled.Link>
                      </li>
                    ))}
                  </ul>
                </Styled.LinkContainer>
              )}

              {links?.length > 1 && (
                <Styled.InfoMessage>
                  During execution, the most recently deployed diagram having the linked process ID will be called.
                </Styled.InfoMessage>
              )}
              {broken && (
                <>
                  <Styled.InfoResourceId>{broken}</Styled.InfoResourceId>
                  <Styled.InfoMessage>
                    A process with this ID could not be found in the current project.
                  </Styled.InfoMessage>
                </>
              )}
              {expression && (
                <>
                  <Styled.InfoResourceId>{expression}</Styled.InfoResourceId>
                  <Styled.InfoMessage>
                    The process ID is defined as an{' '}
                    <Hyperlink href="https://docs.camunda.io/docs/components/concepts/expressions/" target="_blank">
                      expression
                    </Hyperlink>{' '}
                    that will be evaluated at runtime.
                  </Styled.InfoMessage>
                </>
              )}
              {!readOnly && (
                <Styled.Footer>
                  <Styled.FooterActions>
                    <Button kind="danger--ghost" size="sm" onClick={unlinkProcess} data-test="unlink-button">
                      Unlink
                    </Button>
                  </Styled.FooterActions>
                </Styled.Footer>
              )}
            </>
          ) : (
            <Styled.Spinner />
          )}
        </Dropdown>
      ) : (
        <>
          {contextDetails && (
            <TargetSelector
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              startingPoint={contextDetails}
              onSubmit={linkProcess}
              action="link"
              showFiles
              description="Link a process"
              align="bottom"
            />
          )}
        </>
      )}
    </Styled.Container>
  );
});

const CallActivityLinkOverlay = ({ ...props }) => {
  const selector = `.djs-overlays[data-container-id="${props.element.id}"] .call-activity-menu`;
  const container = document.querySelector(selector);
  if (!container) {
    return null;
  }
  // @ts-expect-error TS2739
  return ReactDOM.createPortal(<Overlay {...props} />, container);
};

export default CallActivityLinkOverlay;
